/** IMP-00002 — Master Development Dependency Matrix (mirrors pkg/masterdependency) */

export const IMP_MDDM_ID = 'IMP-00002';

export interface TeamMemberRow {
  role: string;
  responsibility: string;
  focus: string;
}

export interface LayerRow {
  layer: number;
  name: string;
  modules: number;
  status: string;
  progress: number;
}

export interface ModuleDependencyRow {
  module: string;
  prerequisites: string;
  dependents: string;
  blocking: number;
  priority: string;
}

export interface DataAPIEventRow {
  category: string;
  item: string;
  upstream: string;
  downstream: string;
  status: string;
}

export interface AIDependencyRow {
  component: string;
  dependsOn: string;
  usedBy: string;
  risk: string;
}

export interface FrontendBackendRow {
  domain: string;
  item: string;
  dependsOn: string;
  status: string;
}

export interface ParallelCriticalRow {
  type: string;
  item: string;
  detail: string;
  count: number;
}

export interface ExecutiveDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const PLANNING_TEAM = [
  'Chief Enterprise Architect', 'Chief Technology Officer', 'Program Manager', 'Engineering Manager',
  'Backend Lead', 'Frontend Lead', 'Database Architect', 'DevOps Lead', 'QA Lead', 'AI Architect',
];

export const EXTENDED_PLANNING = ['Product Owner', 'Solution Architect'];

export const IMPLEMENTATION_LAYERS = ['Layer 0: Infrastructure', 'Layer 1: Identity', 'Layer 2: Commerce Kernel', 'Layer 3: Marketplace Adapters', 'Layer 4: AI Platform', 'Layer 5: Business Applications'];

export const LAYER_ZERO = ['Infrastructure', 'Repositories', 'CI/CD', 'Development Standards', 'Logging', 'Monitoring', 'Secrets', 'Configuration', 'Event Bus', 'Notifications', 'Feature Flags'];

export const LAYER_ONE = ['Authentication', 'Identity', 'Organizations', 'Tenants', 'Users', 'Roles', 'Permissions', 'RBAC'];

export const LAYER_TWO = ['Products', 'SKUs', 'Brands', 'Categories', 'Attributes', 'Inventory', 'Warehouses', 'Customers', 'Orders'];

export const LAYER_THREE = ['Amazon', 'Flipkart', 'Myntra', 'AJIO', 'Shopify', 'Future Adapters'];

export const LAYER_FOUR = ['Prompt Engine', 'DNA Engine', 'Knowledge Engine', 'AI Agents', 'AI Validation', 'Inference', 'Learning'];

export const LAYER_FIVE = ['Listings', 'Advertising', 'Finance', 'Reports', 'Analytics', 'Automation', 'Customer Success', 'Executive Dashboard'];

export const MODULE_DEPENDENCY_FIELDS = [
  'Module Name', 'Prerequisite Modules', 'Dependent Modules', 'Blocking Dependencies', 'Optional Dependencies',
  'Shared Services', 'Database Dependencies', 'API Dependencies', 'Event Dependencies', 'AI Dependencies',
  'Marketplace Dependencies', 'Infrastructure Dependencies', 'Documentation Dependencies', 'Testing Dependencies',
];

export const DATABASE_DEPENDENCIES = ['Schema Dependencies', 'Foreign Keys', 'Migration Order', 'Lookup Tables', 'Reference Data', 'Master Data', 'Views', 'Indexes', 'Materialized Views', 'Partition Dependencies', 'Audit Tables'];

export const API_DEPENDENCIES = ['Provider', 'Consumer', 'Authentication', 'Authorization', 'Events', 'Request Schema', 'Response Schema', 'Rate Limits', 'Retry Policy', 'Fallback'];

export const EVENT_DEPENDENCIES = ['Producer', 'Consumer', 'Event Schema', 'Version', 'Ordering', 'Retry', 'Dead Letter Queue', 'Idempotency', 'Replay', 'Audit'];

export const AI_DEPENDENCIES = ['Prompt Dependencies', 'DNA Dependencies', 'Knowledge Dependencies', 'Model Dependencies', 'Agent Dependencies', 'Memory Dependencies', 'Context Dependencies', 'Marketplace Dependencies'];

export const FRONTEND_DEPENDENCIES = ['Layouts', 'Navigation', 'Authentication', 'Pages', 'Components', 'Shared Components', 'Design Tokens', 'Hooks', 'Stores', 'Context Providers'];

export const BACKEND_DEPENDENCIES = ['Services', 'Repositories', 'Workers', 'Queues', 'Schedulers', 'Background Jobs', 'Notifications', 'Audit', 'Caching'];

export const WORKFLOW_DEPENDENCIES = ['Workflow Inputs', 'Workflow Outputs', 'Triggers', 'Approvals', 'Notifications', 'AI Steps', 'Rollback', 'Compensation'];

export const PARALLEL_DEVELOPMENT = ['Parallel Modules', 'Sequential Modules', 'Cross-Team Coordination', 'Merge Points', 'Integration Milestones'];

export const CRITICAL_PATH = ['Blocking Modules', 'Highest Risk Components', 'Longest Dependency Chains', 'Infrastructure Dependencies', 'Marketplace Dependencies', 'AI Dependencies'];

export const EXECUTION_PRIORITIES = ['P0 Critical Foundation', 'P1 Core Platform', 'P2 Business Platform', 'P3 AI Platform', 'P4 Optimization', 'P5 Future Enhancements'];

export const ESTIMATION = ['Complexity', 'Engineering Effort', 'Testing Effort', 'Documentation Effort', 'Risk', 'Dependencies', 'Criticality'];

export const TRACEABILITY = ['Business Requirement', 'Architecture', 'Database', 'API', 'Event', 'AI Component', 'Marketplace Connector', 'Governance Document'];

export const ARCHITECTURE_STAGES = [
  'Dependency Team', 'Implementation Layers', 'Module Dependencies', 'Data API Event',
  'AI Dependencies', 'Frontend Backend Workflow', 'Parallel Critical Path', 'Executive Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterDevelopmentPlan: '/platform/master-development-plan',
  masterDependencyMatrix: '/platform/master-dependency-matrix',
  architectureGovernance: '/platform/architecture-governance',
  enterpriseDevOps: '/platform/enterprise-devops',
};

export const DEMO_TEAM: TeamMemberRow[] = [
  { role: 'Chief Enterprise Architect', responsibility: 'Architecture dependency validation', focus: 'Layer sequencing' },
  { role: 'Chief Technology Officer', responsibility: 'Technical dependency approval', focus: 'Critical path' },
  { role: 'Program Manager', responsibility: 'Cross-team coordination', focus: 'Parallel execution' },
  { role: 'Engineering Manager', responsibility: 'Sprint dependency tracking', focus: 'Merge points' },
  { role: 'Database Architect', responsibility: 'Schema & migration order', focus: 'FK dependencies' },
  { role: 'AI Architect', responsibility: 'AI component dependencies', focus: 'Prompt/DNA chain' },
];

export const DEMO_LAYERS: LayerRow[] = [
  { layer: 0, name: 'Infrastructure', modules: 11, status: 'complete', progress: 100 },
  { layer: 1, name: 'Identity & Auth', modules: 8, status: 'complete', progress: 95 },
  { layer: 2, name: 'Commerce Kernel', modules: 9, status: 'active', progress: 78 },
  { layer: 3, name: 'Marketplace Adapters', modules: 6, status: 'active', progress: 65 },
  { layer: 4, name: 'AI Platform', modules: 7, status: 'active', progress: 60 },
  { layer: 5, name: 'Business Applications', modules: 8, status: 'planned', progress: 42 },
];

export const DEMO_MODULES: ModuleDependencyRow[] = [
  { module: 'auth', prerequisites: 'infra, secrets', dependents: 'all services', blocking: 0, priority: 'P0' },
  { module: 'tenant', prerequisites: 'auth, org', dependents: 'commerce, marketplace', blocking: 0, priority: 'P0' },
  { module: 'catalog', prerequisites: 'tenant, brand', dependents: 'amazon-sync, ai-listing', blocking: 2, priority: 'P1' },
  { module: 'amazon-adapter', prerequisites: 'catalog, credentials', dependents: 'orders, inventory', blocking: 1, priority: 'P1' },
  { module: 'ai-platform', prerequisites: 'knowledge, prompt', dependents: 'copilot, agents', blocking: 3, priority: 'P2' },
  { module: 'analytics', prerequisites: 'events, clickhouse', dependents: 'executive dashboard', blocking: 0, priority: 'P2' },
];

export const DEMO_DATA_API_EVENT: DataAPIEventRow[] = [
  { category: 'Database', item: 'tenant schema', upstream: 'org migration', downstream: 'all tenant tables', status: 'ordered' },
  { category: 'Database', item: 'catalog FK chain', upstream: 'brand, product', downstream: 'sku, listing', status: 'ordered' },
  { category: 'API', item: 'POST /products', upstream: 'auth, tenant', downstream: 'catalog events', status: 'contracted' },
  { category: 'Event', item: 'catalog.product.created', upstream: 'catalog service', downstream: 'analytics, ai', status: 'registered' },
  { category: 'Event', item: 'amazon.sync.completed', upstream: 'amazon-sync', downstream: 'inventory, analytics', status: 'registered' },
];

export const DEMO_AI: AIDependencyRow[] = [
  { component: 'Prompt Engine', dependsOn: 'knowledge, brand DNA', usedBy: 'copilot, listing AI', risk: 'medium' },
  { component: 'DNA Engine', dependsOn: 'brand service', usedBy: 'prompt context', risk: 'low' },
  { component: 'AI Agents', dependsOn: 'prompt, decision engine', usedBy: 'workflows', risk: 'high' },
  { component: 'Model Router', dependsOn: 'ai-platform config', usedBy: 'all AI services', risk: 'medium' },
];

export const DEMO_FRONTEND_BACKEND: FrontendBackendRow[] = [
  { domain: 'Frontend', item: 'Navigation shell', dependsOn: 'auth, RBAC', status: 'complete' },
  { domain: 'Frontend', item: 'Design system tokens', dependsOn: 'EDP-00002', status: 'complete' },
  { domain: 'Backend', item: 'Event bus worker', dependsOn: 'redis, postgres', status: 'active' },
  { domain: 'Backend', item: 'Orchestration worker', dependsOn: 'workflow engine', status: 'active' },
  { domain: 'Workflow', item: 'Listing publish flow', dependsOn: 'catalog, amazon-adapter', status: 'planned' },
];

export const DEMO_PARALLEL_CRITICAL: ParallelCriticalRow[] = [
  { type: 'Parallel', item: 'Frontend + Backend modules', detail: '12 active pairs', count: 12 },
  { type: 'Parallel', item: 'Amazon + AI platform', detail: 'Independent after Layer 2', count: 2 },
  { type: 'Critical Path', item: 'auth → tenant → catalog → amazon', detail: 'Longest chain', count: 4 },
  { type: 'Critical Path', item: 'Blocking: ai-platform', detail: '3 downstream blocked', count: 3 },
  { type: 'Risk', item: 'Marketplace API rate limits', detail: 'Mitigation active', count: 1 },
];

export const DEMO_EXECUTIVE: ExecutiveDashboardRow[] = [
  { metric: 'Modules Mapped', value: '86', status: 'complete', score: 100 },
  { metric: 'Dependencies Tracked', value: '312', status: 'current', score: 95 },
  { metric: 'Critical Path Length', value: '4 modules', status: 'managed', score: 88 },
  { metric: 'Parallel Opportunities', value: '12 pairs', status: 'active', score: 92 },
  { metric: 'Blocking Items', value: '6 open', status: 'monitoring', score: 75 },
  { metric: 'P0 Completion', value: '94%', status: 'on_track', score: 94 },
];

export function buildDependencyDashboard() {
  return {
    planning_team_roles: PLANNING_TEAM.length,
    implementation_layers: IMPLEMENTATION_LAYERS.length,
    module_dependencies: MODULE_DEPENDENCY_FIELDS.length,
    priority_levels: EXECUTION_PRIORITIES.length,
    governance_areas: PARALLEL_DEVELOPMENT.length + CRITICAL_PATH.length,
  };
}
